/**
 * What a pad is actually joined to, layer by layer.
 *
 * Not which layers it *occupies* — that is a property of its type and a list of
 * it tells you nothing you did not already know from the word "through". The
 * useful question is narrower and is the one a padstack diagram exists to
 * answer: **on which layers does copper actually touch it, and what net is that
 * copper.**
 *
 * The two come apart exactly where it matters. A via passes through every
 * copper layer including a ground plane, and on the plane it connects only if
 * the pour actually reaches it — a plane with a clearance round the via is
 * copper the via passes through and is not joined to. A four-layer board can
 * have a via present on four layers and connected on two, and nothing in the
 * pad's own parameters says so.
 *
 * Geometry decides it, as it decides everything else about connectivity here:
 * copper that touches is one conductor. The net names come from what is
 * touching rather than from the pad's own `Signal:`, because a pad labelled GND
 * that touches nothing is the fault worth seeing.
 */

import {
  LayerType, isElectrical, padIsSchematic,
  type CadDocument, type ComponentEntity, type Entity, type PadEntity, type Vertex,
} from '../model.ts';
import { isPadMaster, occupiedLayers } from '../geom/board.ts';
import { padOutline } from '../geom/pad.ts';
import { placeInComponent } from './netlist.ts';

/** One copper layer, and this pad's relationship to it. */
export interface StackLayer {
  /** Index into the document's layer table. */
  layer: number;
  name: string;
  /** Screen colour, 0xRRGGBB. */
  colour: number;
  type: LayerType;
  /** The pad has copper on this layer at all. */
  present: boolean;
  /** Nets of the copper touching it here, in the order found. */
  nets: string[];
  /** How many separate pieces of copper touch it here. */
  touching: number;
}

/** Two pieces of copper are joined when the gap between them closes to this. */
const EPS = 1e-7;

/** Distance between two segments. */
function segSeg(
  ax1: number, ay1: number, ax2: number, ay2: number,
  bx1: number, by1: number, bx2: number, by2: number,
): number {
  // Crossing segments are at zero distance; the endpoint tests below never
  // report that on their own.
  const d = (ax2 - ax1) * (by2 - by1) - (ay2 - ay1) * (bx2 - bx1);
  if (Math.abs(d) > 1e-12) {
    const t = ((bx1 - ax1) * (by2 - by1) - (by1 - ay1) * (bx2 - bx1)) / d;
    const u = ((bx1 - ax1) * (ay2 - ay1) - (by1 - ay1) * (ax2 - ax1)) / d;
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return 0;
  }
  return Math.min(
    ptSeg(ax1, ay1, bx1, by1, bx2, by2), ptSeg(ax2, ay2, bx1, by1, bx2, by2),
    ptSeg(bx1, by1, ax1, ay1, ax2, ay2), ptSeg(bx2, by2, ax1, ay1, ax2, ay2),
  );
}

function ptSeg(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = dx * dx + dy * dy;
  if (len === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** Is the point inside this ring? Even-odd, which is enough for a pad or pour. */
function inside(px: number, py: number, poly: Vertex[]): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if ((a.y > py) !== (b.y > py)
      && px < ((b.x - a.x) * (py - a.y)) / (b.y - a.y) + a.x) hit = !hit;
  }
  return hit;
}

/** Distance from a segment to a closed ring; zero if it is inside. */
function segRing(
  x1: number, y1: number, x2: number, y2: number, ring: Vertex[],
): number {
  if (ring.length < 3) return Infinity;
  if (inside(x1, y1, ring) || inside(x2, y2, ring)) return 0;
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    best = Math.min(best, segSeg(x1, y1, x2, y2, a.x, a.y, b.x, b.y));
    if (best <= 0) return 0;
  }
  return best;
}

/** Distance between two closed rings; zero if either contains the other. */
function ringRing(a: Vertex[], b: Vertex[]): number {
  if (a.length < 3 || b.length < 3) return Infinity;
  if (inside(a[0]!.x, a[0]!.y, b) || inside(b[0]!.x, b[0]!.y, a)) return 0;
  let best = Infinity;
  for (let i = 0; i < a.length; i++) {
    const p = a[i]!;
    const q = a[(i + 1) % a.length]!;
    best = Math.min(best, segRing(p.x, p.y, q.x, q.y, b));
    if (best <= 0) return 0;
  }
  return best;
}

/** A primitive lifted into world space, with the layer it conducts on. */
interface Piece {
  entity: Entity;
  layer: number;
  /** True when it is on the pad master and therefore on every copper layer. */
  everywhere: boolean;
  net: string;
  ring?: Vertex[];
  seg?: { x1: number; y1: number; x2: number; y2: number; half: number };
  box: { x1: number; y1: number; x2: number; y2: number };
}

function boxOf(pts: { x: number; y: number }[], grow = 0): Piece['box'] {
  let x1 = Infinity; let y1 = Infinity; let x2 = -Infinity; let y2 = -Infinity;
  for (const p of pts) {
    x1 = Math.min(x1, p.x); x2 = Math.max(x2, p.x);
    y1 = Math.min(y1, p.y); y2 = Math.max(y2, p.y);
  }
  return { x1: x1 - grow, y1: y1 - grow, x2: x2 + grow, y2: y2 + grow };
}

/** Every conducting primitive on the board, in world space. */
function pieces(doc: CadDocument): Piece[] {
  const out: Piece[] = [];
  const walk = (list: Entity[], owner: ComponentEntity | null): void => {
    for (const e of list) {
      if (e.kind === 'component') { walk(e.children, e); continue; }
      const at = (p: Vertex): Vertex => (owner ? placeInComponent(p, owner) : p);
      const net = e.signal ?? '';
      const everywhere = isPadMaster(e);

      if (e.kind === 'pad') {
        if (padIsSchematic(e.type)) continue;
        const here = at({ x: e.x, y: e.y });
        const ring = padOutline({
          ...e, x: here.x, y: here.y,
          rotation: (e.rotation ?? 0) + (owner?.rotation ?? 0),
        });
        if (ring.length < 3) continue;
        out.push({ entity: e, layer: e.layer, everywhere, net, ring, box: boxOf(ring) });
        continue;
      }
      if (e.kind === 'line') {
        const a = at({ x: e.x1, y: e.y1 });
        const b = at({ x: e.x2, y: e.y2 });
        const half = e.width / 2;
        out.push({
          entity: e, layer: e.layer, everywhere, net,
          seg: { x1: a.x, y1: a.y, x2: b.x, y2: b.y, half },
          box: boxOf([a, b], half),
        });
        continue;
      }
      if (e.kind === 'fill' && e.vertices.length >= 3) {
        const ring = e.vertices.map(at);
        out.push({ entity: e, layer: e.layer, everywhere, net, ring, box: boxOf(ring) });
      }
    }
  };
  walk(doc.entities, null);
  return out;
}

function overlap(a: Piece['box'], b: Piece['box'], slack = 0): boolean {
  return a.x1 - slack <= b.x2 && a.x2 + slack >= b.x1
    && a.y1 - slack <= b.y2 && a.y2 + slack >= b.y1;
}

/**
 * The pad's stack: every copper layer, whether the pad is on it, and what
 * touches it there.
 *
 * `owner` is the component the pad belongs to, when it belongs to one, so a pin
 * inside a turned footprint is measured where it really sits.
 */
export function padStack(
  doc: CadDocument, pad: PadEntity, owner: ComponentEntity | null = null,
): StackLayer[] {
  const copper = doc.layers
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => isElectrical(l.type));
  if (copper.length === 0 || doc.kind !== 'pcb') return [];

  const here = owner ? placeInComponent({ x: pad.x, y: pad.y }, owner) : { x: pad.x, y: pad.y };
  const ring = padOutline({
    ...pad, x: here.x, y: here.y,
    rotation: (pad.rotation ?? 0) + (owner?.rotation ?? 0),
  });
  const on = new Set(occupiedLayers(pad, doc));
  const box = boxOf(ring);

  // Only what is near it, and never the pad itself.
  const near = pieces(doc).filter(
    (p) => p.entity.id !== pad.id && overlap(p.box, box, EPS));

  return copper.map(({ l, i }) => {
    const present = on.has(i);
    const nets: string[] = [];
    let touching = 0;

    if (present) {
      for (const p of near) {
        // Conducts on this layer? A pad-master pad is on all of them.
        if (!p.everywhere && p.layer !== i) continue;

        let gap = Infinity;
        if (p.ring) gap = ringRing(ring, p.ring);
        else if (p.seg) {
          gap = segRing(p.seg.x1, p.seg.y1, p.seg.x2, p.seg.y2, ring) - p.seg.half;
        }
        if (gap > EPS) continue;

        touching++;
        const net = p.net || p.entity.signal || '';
        if (net && !nets.includes(net)) nets.push(net);
      }
    }

    return {
      layer: i, name: l.name, colour: l.screen, type: l.type,
      present, nets, touching,
    };
  });
}
